import './style.scss'
import URI from 'urijs'
import Backbone from '@polleverywhere/backbone'
import Latex from '@polleverywhere/core/lib/latex'
import { cleanString, proxiedImageUrl } from '@polleverywhere/core/lib/util'
import { normalizeScale } from '@polleverywhere/viz-utils'
import { colorToRGBA } from '@polleverywhere/core/lib/color'

const HIDE_CHART_TITLE_DISPLAY_STATES = [
  'title'
]

function shouldBeVisible () {
  if (this.flashOptions.get('showTitle')) {
    return !HIDE_CHART_TITLE_DISPLAY_STATES.includes(this.model.get('display_state'))
  } else {
    return false
  }
}

export class ChartTitle extends Backbone.View {
  get className () { return 'chart--title' }

  constructor (...args) {
    super(...args)
    this.adjustRightPadding = this.adjustRightPadding.bind(this)
    this.compatibleColor = this.compatibleColor.bind(this)
    this.customize = this.customize.bind(this)
    this.customizeBackgroundColor = this.customizeBackgroundColor.bind(this)
    this.customizeHeaderScale = this.customizeHeaderScale.bind(this)
    this.customizeTitleColor = this.customizeTitleColor.bind(this)
    this.initialize = this.initialize.bind(this)
    this.positionTitle = this.positionTitle.bind(this)
    this.render = this.render.bind(this)
    this.renderLogo = this.renderLogo.bind(this)
    this.scaleImage = this.scaleImage.bind(this)
    this.toggleVisibility = this.toggleVisibility.bind(this)
  }

  initialize ({ settings, dimensions }) {
    this.settings = settings
    this.dimensions = dimensions
    this.flashOptions = this.model.flashOptions()

    this.listenTo(this.flashOptions, 'change:headerLogoUrl', this.renderLogo)
    this.listenTo(this.flashOptions, 'change:headerLogoPlacement', () => {
      this.positionTitle()
      this.trigger('resize')
    })
    this.listenTo(this.flashOptions, 'change:title_scale', () => {
      this.customizeHeaderScale()
      this.trigger('resize')
    })
    this.listenTo(this.flashOptions, 'change:header_title_color', this.customizeTitleColor)
    this.listenTo(this.flashOptions, 'change:header_top_background_color', this.customizeBackgroundColor)
    this.listenTo(this.flashOptions, 'change:header_bottom_background_color', this.customizeBackgroundColor)
    this.listenTo(this.flashOptions, 'change:headerTransparent', this.customizeBackgroundColor)
    this.listenTo(this.flashOptions, 'change:showTitle', this.toggleVisibility)
    this.listenTo(this.model, 'change:title', this.renderTitle)
    this.listenTo(this.dimensions, 'change:height', this.scaleImage)
    this.listenTo(this.dimensions, 'change:marginRight', this.adjustRightPadding)
  }

  compatibleColor (flashOptionsProperty, alpha = 1) {
    return colorToRGBA(this.flashOptions.get(flashOptionsProperty, alpha), alpha)
  }

  customizeBackgroundColor () {
    const backgroundColor = this.flashOptions.get('background_color')
    const gradientTop = this.flashOptions.get('header_top_background_color')
    const gradientBottom = this.flashOptions.get('header_bottom_background_color')

    const gradientSameAsBackground = gradientTop === gradientBottom && gradientTop === backgroundColor
    const isTransparent = this.flashOptions.has('headerTransparent') && this.flashOptions.get('headerTransparent')
    let gradient = `linear-gradient(${gradientTop}, ${gradientBottom})`

    if (isTransparent) {
      gradient = 'transparent'
    } else if (gradientSameAsBackground) {
      gradient = backgroundColor
    }

    this.$el.css({ background: gradient })
  }

  toggleVisibility () {
    this.customizeBackgroundColor()

    this.$el.toggleClass('hidden', !shouldBeVisible.call(this))
  }

  renderTitle () {
    if (this.$title) {
      this.$title.remove()
      delete this.$title
    }

    const title = this.model.get('title')

    // TODO: Need to figure out why the dimension model is reaching into the
    // DOM, specifically into this view and getting the font-size of the title
    // even if the flashoption showTitle is false.
    // if (!this.flashOptions.get('showTitle')) return

    this.$title = Backbone.$('<span />').addClass('title')
    this.$el.append(this.$title)

    if (Latex.isLatex(title)) {
      Latex.renderInto(this.$title, title, {
        cleanWhitelist: ['strong', 'em', 'br']
      }).then(() => {
        this.trigger('render:latex', this.$title)
      })
    } else {
      this.$title.html(cleanString(title, { whitelist: ['strong', 'em', 'br'] }))
    }

    this.toggleVisibility()
  }

  customize () {
    this.customizeBackgroundColor()
    this.customizeTitleColor()
    this.customizeHeaderScale()
  }

  customizeTitleColor () {
    this.$el.css('color', this.flashOptions.get('header_title_color'))
  }

  customizeHeaderScale () {
    const fontSize = normalizeScale(this.flashOptions.get('title_scale'), 0, 1, 2.7, 18)
    this.$el.css('fontSize', `${fontSize}em`)
    this.scaleImage()
  }

  scaleImage () {
    if (!this.$logo) { return }

    if (this.image) {
      delete this.image
      const imageScale = normalizeScale(this.flashOptions.get('title_scale'), 0, 1, 0.07, 0.5) * this.dimensions.height()
      const targetSize = Math.round(imageScale)
      this.$logo.css({
        maxHeight: `${targetSize}px`,
        maxWidth: `${33.3}%`
      })
      this.trigger('resize')
    } else {
      this.image = new Image()
      this.image.onload = this.scaleImage
      this.image.src = this.$logo.attr('src')
    }
  }

  adjustRightPadding () {
    const val =
      this.settings.hasFixedControls() && !this.settings.get('fullscreen')
        ? 75
        : ''

    this.$el.css({ paddingRight: val })
  }

  positionTitle () {
    const headerLogoPlacement = this.flashOptions.get('headerLogoPlacement')
    this.$el.removeClass('center left right')
    this.$el.addClass(headerLogoPlacement)
  }

  renderLogo () {
    if (this.$logo) {
      this.$logo.remove()
      delete this.$logo
    }

    const headerLogoUrl = this.flashOptions.get('headerLogoUrl')
    if (!this.flashOptions.get('showTitle')) return
    if (!headerLogoUrl) { return }

    const url = proxiedImageUrl(URI(headerLogoUrl))
    this.$logo = Backbone.$('<img />')
      .addClass('image')
      .attr('src', url)

    if (/Edge\/|Trident\/|MSIE /i.test(window.navigator.userAgent)) {
      this.$logo.css({ display: 'block' })
    }

    this.$el.prepend(this.$logo)
    this.scaleImage()

    return this
  }

  render () {
    this.$el.empty()
    this.positionTitle()
    this.renderLogo()
    this.renderTitle()
    this.customize()
    this.adjustRightPadding()
    this.trigger('render')
    return this
  }
}
